import { useState, useLayoutEffect } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import { LABELS, CONTENTS } from "./constants";
import { Box } from "@cloudscape-design/components-themed";
import { COMMON_LABELS } from "../../default-constants";
import Preference from "../Preference";
import { DEFAULT_PAGE_SIZE } from "../../../configs/constants";
import { getPagedItems } from "../../../utils/common";
import Pagination from "../Pagination";
import { CompanyCodeCheckErrorInfo } from "../../../features/api-interfaces";
import { formatLabel } from "../../../utils/format";

export interface ErrorMessageItem {
  no: number;
  siteId: string;
  companyCode: string;
  occurredPlace: string;
}

export interface CheckResult {
  isError: boolean;
  itemList: ErrorMessageItem[];
}

export interface CompanyCodeErrorTableProps {
  data: ErrorMessageItem[];
}

/**
 * エラーメッセージ作成処理
 * @param messageList メッセージリスト
 * @returns エラーメッセージリスト
 */
export const getErrorMessageItemList = (
  errorInfoList: CompanyCodeCheckErrorInfo[]
) => {
  // エラーメッセージリスト作成
  let errorMessageList: ErrorMessageItem[] = [];

  for (const [index, errorInfo] of errorInfoList.entries()) {
    const errorData: ErrorMessageItem = {
      no: index + 1,
      siteId: errorInfo.siteId,
      companyCode: errorInfo.companyCode,
      occurredPlace: formatLabel(CONTENTS.ERROR_ROW, [errorInfo.errorRow]),
    };
    errorMessageList.push(errorData);
  }

  return errorMessageList;
};

/**
 * エラー発生時のテーブルを表示する。
 *
 * @param props.data エラーデータリスト（ErrorMessageItem[]型）
 * @returns エラーメッセージ表示テーブル
 */
const CompanyCodeErrorTable = (props: CompanyCodeErrorTableProps) => {
  const columnDefinitions = [
    {
      id: "no",
      header: LABELS.NO,
      cell: (e: any) => e.no,
      width: 110,
    },
    {
      id: "siteId",
      header: LABELS.SITE_ID,
      cell: (e: any) => e.siteId,
    },
    {
      id: "companyCode",
      header: LABELS.COMPANY_CODE,
      cell: (e: any) => e.companyCode,
    },
    {
      id: "occurredPlace",
      header: LABELS.OCCURRED_PLACE,
      cell: (e: any) => e.occurredPlace,
    },
  ];
  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>(
    getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE)
  );
  const [allItems, setAllItems] = useState<Object[]>(props.data);

  useLayoutEffect(() => {
    if (props.data !== undefined) {
      setPagedItems(getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(props.data);
      setCurrentPageIndex(1);
    }
  }, [props.data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (props.data !== undefined) {
      setPagedItems(props.data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <Box color="text-status-error" variant="h4">
          {LABELS.ERROR_TEXT_LABEL1}
          <br/>
          {LABELS.ERROR_TEXT_LABEL2}
        </Box>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default CompanyCodeErrorTable;
